import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AssignBatchModal({ selectedIds, onClose, onAssigned }) {
  const [reviewers, setReviewers] = useState([]);
  const [reviewerId, setReviewerId] = useState("");
  const [name, setName] = useState(`Batch ${new Date().toLocaleDateString()}`);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/users/reviewers");
        if (cancelled) return;
        setReviewers(data);
        if (data[0]) setReviewerId(data[0].id);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load reviewers");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async () => {
    if (!reviewerId) {
      toast.error("Select a reviewer");
      return;
    }
    setBusy(true);
    try {
      await api.post("/batches", {
        name,
        reviewer_id: reviewerId,
        question_ids: selectedIds,
        notes,
      });
      toast.success(`Assigned ${selectedIds.length} questions`);
      onAssigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        data-testid="assign-modal"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Assign Batch to Reviewer
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Creating a batch of <strong>{selectedIds.length}</strong> question
          {selectedIds.length === 1 ? "" : "s"}.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Batch Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Reviewer
            </label>
            <select
              data-testid="assign-reviewer-select"
              value={reviewerId}
              onChange={(e) => setReviewerId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            >
              {reviewers.length === 0 && <option value="">No reviewers available</option>}
              {reviewers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} · {r.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            data-testid="assign-submit-btn"
            onClick={submit}
            disabled={busy || !reviewerId}
            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold"
          >
            {busy ? "Assigning…" : "Assign Batch"}
          </button>
        </div>
      </div>
    </div>
  );
}
